import {
  CANNONS,
  CARS,
  DEATH_EXPLOSIONS,
  ENGINE_SOUNDS,
  NITRO_EFFECTS,
  PROJECTILES,
  TOMBSTONES,
  WHEELS,
  getCar,
} from './data.js';

export const GARAGE_STORAGE_KEY = 'turbo-crown-guest-garage-v3';
export const GARAGE_SCHEMA_VERSION = 3;
const LEGACY_STORAGE_KEYS = Object.freeze(['toybox-garage-v2','toybox-garage-v1']);

const SLOT_DEFINITIONS = Object.freeze({
  wheelId: { ownedKey:'ownedWheels', catalog:WHEELS, fallback:'defaultWheelId' },
  engineSoundId: { ownedKey:'ownedEngineSounds', catalog:ENGINE_SOUNDS, fallback:'defaultEngineSoundId' },
  cannonId: { ownedKey:'ownedCannons', catalog:CANNONS, fallback:'defaultCannonId' },
  projectileId: { ownedKey:'ownedProjectiles', catalog:PROJECTILES, fallback:'defaultProjectileId' },
  nitroId: { ownedKey:'ownedNitros', catalog:NITRO_EFFECTS, fallback:'defaultNitroId' },
  explosionId: { ownedKey:'ownedExplosions', catalog:DEATH_EXPLOSIONS, fallback:'defaultExplosionId' },
  tombstoneId: { ownedKey:'ownedTombstones', catalog:TOMBSTONES, fallback:'defaultTombstoneId' },
});

function allIds(catalog) {
  return catalog.map((item) => item.id);
}

function starterIds(catalog) {
  return catalog.filter((item) => item.starter !== false).map((item) => item.id);
}

function sanitizeOwned(source, key, catalog, { includeDefaults = true, startersOnly = false } = {}) {
  const allowedIds=startersOnly?starterIds(catalog):allIds(catalog);
  const known = new Set(allowedIds);
  const requested = Array.isArray(source[key]) ? [...source[key]] : [];
  if (includeDefaults) requested.push(...starterIds(catalog));
  const result = [...new Set(requested)].filter((id) => known.has(id));
  if (!result.length) result.push(allowedIds[0]||catalog[0].id);
  return result;
}

function defaultLoadout(car) {
  return {
    wheelId: car.defaultWheelId,
    engineSoundId: car.defaultEngineSoundId,
    cannonId: car.defaultCannonId,
    projectileId: car.defaultProjectileId,
    nitroId: car.defaultNitroId,
    explosionId: car.defaultExplosionId,
    tombstoneId: car.defaultTombstoneId,
  };
}

export function createStarterGarage() {
  const garage = {
    version: GARAGE_SCHEMA_VERSION,
    ownedCars: starterIds(CARS),
    ownedWheels: starterIds(WHEELS),
    ownedEngineSounds: starterIds(ENGINE_SOUNDS),
    ownedCannons: starterIds(CANNONS),
    ownedProjectiles: starterIds(PROJECTILES),
    ownedNitros: starterIds(NITRO_EFFECTS),
    ownedExplosions: starterIds(DEATH_EXPLOSIONS),
    ownedTombstones: starterIds(TOMBSTONES),
    loadouts: {},
  };
  for (const car of CARS.filter((item) => item.starter !== false)) garage.loadouts[car.id] = defaultLoadout(car);
  return garage;
}

export function sanitizeGarage(source = {}, { includeStarters = true, startersOnly = false } = {}) {
  const allowedCarIds=startersOnly?starterIds(CARS):allIds(CARS);
  const knownCars = new Set(allowedCarIds);
  const ownedCars = [...new Set(Array.isArray(source.ownedCars) ? source.ownedCars : includeStarters ? starterIds(CARS) : [])]
    .filter((id) => knownCars.has(id));
  // Mientras la colección inicial siga siendo gratuita, los autos nuevos deben
  // aparecer también en garages creados por versiones anteriores del juego.
  // Más adelante los vehículos de tienda marcarán `starter: false`.
  if (includeStarters) {
    CARS.filter((car) => car.starter !== false).forEach((car) => {
      if (!ownedCars.includes(car.id)) ownedCars.push(car.id);
    });
  }
  if (!ownedCars.length) ownedCars.push(allowedCarIds[0]||CARS[0].id);

  const garage = {
    version: GARAGE_SCHEMA_VERSION,
    ownedCars,
    ownedWheels: sanitizeOwned(source, 'ownedWheels', WHEELS, { includeDefaults: includeStarters, startersOnly }),
    ownedEngineSounds: sanitizeOwned(source, 'ownedEngineSounds', ENGINE_SOUNDS, { includeDefaults: includeStarters, startersOnly }),
    ownedCannons: sanitizeOwned(source, 'ownedCannons', CANNONS, { includeDefaults: includeStarters, startersOnly }),
    ownedProjectiles: sanitizeOwned(source, 'ownedProjectiles', PROJECTILES, { includeDefaults: includeStarters, startersOnly }),
    ownedNitros: sanitizeOwned(source, 'ownedNitros', NITRO_EFFECTS, { includeDefaults: includeStarters, startersOnly }),
    ownedExplosions: sanitizeOwned(source, 'ownedExplosions', DEATH_EXPLOSIONS, { includeDefaults: includeStarters, startersOnly }),
    ownedTombstones: sanitizeOwned(source, 'ownedTombstones', TOMBSTONES, { includeDefaults: includeStarters, startersOnly }),
    loadouts: {},
  };

  for (const carId of ownedCars) {
    const car = getCar(carId);
    const requested = { ...(source.loadouts?.[carId] || {}) };
    // Migración transparente desde la primera versión, que solo guardaba ruedas.
    if (!requested.wheelId && source.equippedWheels?.[carId]) requested.wheelId = source.equippedWheels[carId];
    const loadout = {};
    for (const [slot, definition] of Object.entries(SLOT_DEFINITIONS)) {
      const owned = garage[definition.ownedKey];
      const fallback = car[definition.fallback] || definition.catalog[0].id;
      loadout[slot] = owned.includes(requested[slot])
        ? requested[slot]
        : owned.includes(fallback) ? fallback : owned[0];
    }
    garage.loadouts[carId] = loadout;
  }
  return garage;
}

export function loadGarage(storage = globalThis.localStorage) {
  try {
    const current = storage?.getItem(GARAGE_STORAGE_KEY);
    const legacy = LEGACY_STORAGE_KEYS.map((key)=>storage?.getItem(key)).find(Boolean);
    const garage=sanitizeGarage(JSON.parse(current || legacy || 'null') || {},{
      includeStarters:true,
      startersOnly:true,
    });
    storage?.setItem(GARAGE_STORAGE_KEY,JSON.stringify(garage));
    LEGACY_STORAGE_KEYS.forEach((key)=>storage?.removeItem(key));
    return garage;
  } catch {
    const garage=createStarterGarage();
    try{
      storage?.setItem(GARAGE_STORAGE_KEY,JSON.stringify(garage));
      LEGACY_STORAGE_KEYS.forEach((key)=>storage?.removeItem(key));
    }catch{/* El modo invitado puede funcionar aunque el storage esté bloqueado. */}
    return garage;
  }
}

export function saveGarage(garage, storage = globalThis.localStorage) {
  const safeGarage = sanitizeGarage(garage,{includeStarters:true,startersOnly:true});
  storage?.setItem(GARAGE_STORAGE_KEY, JSON.stringify(safeGarage));
  return safeGarage;
}

export function getCarLoadout(garage, carId) {
  const car = getCar(carId);
  const loadout = garage.loadouts?.[car.id] || defaultLoadout(car);
  return { ...defaultLoadout(car), ...loadout };
}

export function equipCosmetic(garage, carId, slot, itemId) {
  const definition = SLOT_DEFINITIONS[slot];
  if (!definition || !garage.ownedCars.includes(carId) || !garage[definition.ownedKey].includes(itemId)) return false;
  garage.loadouts[carId] ||= defaultLoadout(getCar(carId));
  garage.loadouts[carId][slot] = itemId;
  return true;
}

export function getOwnedCatalog(garage, slot) {
  const definition = SLOT_DEFINITIONS[slot];
  if (!definition) return [];
  const owned = new Set(garage[definition.ownedKey]);
  return definition.catalog.filter((item) => owned.has(item.id));
}

export function unlockCar(garage, carId) {
  const car = CARS.find((item) => item.id === carId);
  if (!car) return false;
  if (!garage.ownedCars.includes(carId)) garage.ownedCars.push(carId);
  garage.loadouts[carId] ||= defaultLoadout(car);
  return true;
}

export function unlockCosmetic(garage, slot, itemId) {
  const definition = SLOT_DEFINITIONS[slot];
  if (!definition || !definition.catalog.some((item) => item.id === itemId)) return false;
  if (!garage[definition.ownedKey].includes(itemId)) garage[definition.ownedKey].push(itemId);
  return true;
}

// Compatibilidad con la primera interfaz y con llamadas existentes.
export const getEquippedWheelId = (garage, carId) => getCarLoadout(garage, carId).wheelId;
export const equipWheel = (garage, carId, wheelId) => equipCosmetic(garage, carId, 'wheelId', wheelId);
